import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import MatchTable from './MatchTable';

/**
 * Generic flat round-by-round match list. Used standalone for Round Robin
 * (its only view — there's no bracket shape to switch to) and as the "List"
 * alternative for graphical formats — see ScheduleResultView.jsx for every
 * call site (also reused internally by CrossGroupView, LadderView, and Page
 * Playoff's League view).
 *
 * A direct port of production's own schedule display — both the mobile
 * card layout (ScheduleTab.jsx:1666-1802) and the desktop table layout
 * (:1808-1940) — rather than an original design. See MatchTable.jsx for
 * the actual markup/classes/round-collapse port.
 *
 * `orderOverrides`/`onReorderRow`, `roundOverrides`/`onApplyRoundOverride`,
 * and `rowOverrides`/`onApplyRowOverride` are all optional — a caller only
 * needs to pass them to turn on that feature (row reorder, the round/match
 * gear-icon settings modal); omitted, the relevant controls just don't
 * render. The overrides objects are already scoped to THIS list by the
 * caller (whichever slice of the hook's `matchOrderOverrides`/
 * `roundOverrides`/`rowOverrides` this particular MatchListView owns, and
 * callbacks already bound to this list's own key) — this component and
 * everything below it never needs to know the list's own key. `courts`,
 * `sportType`, `filters`, and `currentUserId` are global (the same court
 * list / sport / Filters-panel selections / logged-in identity apply to
 * every list a format renders), not per-list — see MatchTable.jsx for where
 * `filters` actually narrows the row set and drives Match Detail's
 * Previous/Next.
 */
const MatchListView = ({
  rounds,
  title,
  orderOverrides,
  onReorderRow,
  courts,
  sportType,
  tournamentMatchSettings,
  roundOverrides,
  rowOverrides,
  onApplyRoundOverride,
  onApplyRowOverride,
  filters,
  currentUserId,
  onClearFilters,
  tournamentVO,
  startScoring,
  openLiveGame,
  canManageThisSchedule,
  updateMatchSchedule,
  deleteMatchRow,
  assignMatchParticipants,
  addMatch,
  fetchAllParticipants,
  participantType,
}) => {
  const { theme } = useTheme();

  return (
    <div>
      {title && <div className={`mb-2 text-sm font-bold ${theme.text}`}>{title}</div>}
      <MatchTable
        rounds={rounds}
        orderOverrides={orderOverrides}
        onReorderRow={onReorderRow}
        courts={courts}
        sportType={sportType}
        tournamentMatchSettings={tournamentMatchSettings}
        roundOverrides={roundOverrides}
        rowOverrides={rowOverrides}
        onApplyRoundOverride={onApplyRoundOverride}
        onApplyRowOverride={onApplyRowOverride}
        filters={filters}
        currentUserId={currentUserId}
        onClearFilters={onClearFilters}
        tournamentVO={tournamentVO}
        startScoring={startScoring}
        openLiveGame={openLiveGame}
        canManageThisSchedule={canManageThisSchedule}
        updateMatchSchedule={updateMatchSchedule}
        deleteMatchRow={deleteMatchRow}
        assignMatchParticipants={assignMatchParticipants}
        addMatch={addMatch}
        fetchAllParticipants={fetchAllParticipants}
        participantType={participantType}
      />
    </div>
  );
};

export default MatchListView;
